import {ParsedUrlQuery} from 'querystring';
import {i18n, TFunction} from 'i18next';
import {RefCallback, MutableRefObject} from 'react';
import {call, put, take, delay, fork, all} from 'redux-saga/effects';
import {
  IS_PURCHASE_TAB_TOGGLED_LS_KEY,
  LS_KEY_LAST_EPISODE,
  TIME_UNIT_TEXT_KEY_MAP,
} from './constant';
import {descriptKeys, decryptImage} from './decrypt';
import {actions as commonSubActions} from './state/action';
import {
  IEntryType,
  IEpisodeType,
  IExpireDate,
  RecentItem,
  IUseTicketParams,
  IWaitfree,
  IWaitfreeTimeUnit,
} from './type';
import {API_AUTH, getAccessToken, saveReturnPage} from '../auth/common';
import {actions as authActions} from '../auth/state/action';
import {IUser} from '../auth/type';
import {pAlert} from '../common/component/PAlert';
import {
  IS_CLIENT,
  REGION,
  UNKNOWN,
  RECENT_ITEM_LIST_KEY,
  ACCESS_TOKEN_KEY,
  AUTH_HOST,
} from '../common/constant';
import {Page} from '../common/constant/page';
import {pToast} from '../common/container/PToast';
import {
  copyToClipboard,
  getIsIdVerificationCompleted,
  showAuthModal,
  showIdVerificationModal,
} from '../common/function';
import {clientT} from '../common/i18n';
import {actions as commonActions} from '../common/state/action';
import {getClientStore} from '../common/store';
import {IRegion, IUserAgent} from '../common/type';
import {ButtonSet} from '../common/type/alert';
import callApi from '../common/util/api';
import {deleteCookie} from '../common/util/cookie';
import {
  MINUTES_OF_A_DAY,
  MINUTES_OF_AN_HOUR,
  SECONDS_OF_A_DAY,
  getRemainText,
} from '../common/util/date';
import {
  getQueryObject,
  getRouterQueryObject,
  getUrlWithFilteredQuery,
} from '../common/util/PageRouter';
import {ReduxAction} from '../common/util/redux';
import {typedSelect} from '../common/util/saga';
import {
  clearLocalStorage,
  deleteLocalStorage,
  getLocalStorageBool,
  getLocalStorageObject,
  setLocalStorageBool,
  setLocalStorageObject,
} from '../common/util/storage';
import {convertHexToRgb} from '../common/util/string';
import {
  sendEvent,
  sendExtra,
  sendPageView,
  sendViewImp,
} from '../common/util/tracking';
import {actions as moreActions} from '../more/state/action';
import {IViewerDecryptInfo} from '../viewer/type';

function getCsMailBody(userAgent?: IUserAgent, user?: IUser) {
  if (!IS_CLIENT) {
    return '';
  }
  const data = [
    {
      key: 'more_customer_inquiry_info_id',
      text: user && user.csId,
    },
    {
      key: 'more_customer_inquiry_info_browser',
      text: userAgent && `${userAgent.browserName} ${userAgent.version}`,
    },
    {
      key: 'more_customer_inquiry_info_device',
      text: userAgent && userAgent.deviceName,
    },
    {
      key: 'more_customer_inquiry_info_version_ios',
      text: userAgent && userAgent.isIos && userAgent.osVersion,
    },
    {
      key: 'more_customer_inquiry_info_version_aos',
      text: userAgent && userAgent.isAndroid && userAgent.osVersion,
    },
  ].filter(item => !!item.text && !item.text.includes(UNKNOWN));
  const lineBreak = '%0D%0A';
  return `
${clientT('more_customer_inquiry_global_question_1')}${lineBreak}${lineBreak}
${clientT('more_customer_inquiry_global_question_2')}${lineBreak}${lineBreak}
${clientT(
  'more_customer_inquiry_global_question_3'
)}${lineBreak}${lineBreak}${lineBreak}${lineBreak}
${clientT('more_customer_inquiry_global_agreement_notice')}${lineBreak}
${data.map(item => `${clientT(item.key)}: ${item.text}`).join(' / ')}
`.trim();
}

export const getCsMailUrl = ({
  userAgent,
  user,
  email,
}: {
  userAgent?: IUserAgent;
  user?: IUser;
  email?: string;
}) => {
  let mailto = '';

  if (email) {
    mailto = `mailto:${email}`;
  } else {
    const mailParams = `?subject=${clientT(
      'more_customer_inquiry_global_title'
    )}&body=${getCsMailBody(userAgent, user)}`;
    mailto = `mailto:${CS_EMAIL[REGION]}${mailParams}`;
  }

  return IS_CLIENT ? mailto : '';
};

export function createKakaowebtoonWeb() {
  if ('kakaowebtoonWeb' in window) {
    return;
  }

  function pageViewEvent(json: string) {
    sendPageView(JSON.parse(json));
  }
  function trackExtraEvent(json: string) {
    sendExtra(JSON.parse(json));
  }
  function trackEvent(json: string) {
    sendEvent(JSON.parse(json));
  }
  function viewImp(json: string) {
    sendViewImp(JSON.parse(json));
  }

  window.kakaowebtoonWeb = {
    pageViewEvent,
    trackExtraEvent,
    trackEvent,
    viewImp,
  };
}

export function copyText(text: string) {
  copyToClipboard({
    copyText: text,
    onSuccess: () =>
      pToast({
        toastId: 'more_copy_toast',
        desc: clientT('more_copy_toast'),
      }),
  });
}

const agePopupOkContentIdsForNonLogin: number[] = [];
export async function checkAgeAndShowPopup({
  contentId,
  ageRating,
  isLogin,
}: {
  contentId: number;
  ageRating: number;
  isLogin: boolean;
}) {
  if (ageRating < 18) {
    return true;
  }

  const agePopupOkContentIds =
    getLocalStorageObject<number[]>({
      key: 'viewer_age_popup_ok_content_ids',
    }) || [];
  if (
    agePopupOkContentIds.includes(contentId) ||
    agePopupOkContentIdsForNonLogin.includes(contentId)
  ) {
    return true;
  }

  const {okClicked} = await pAlert({
    title: t => t('contenthome_age_warning_popup_title'),
    desc: t => t('contenthome_age_warning_popup_contents'),
    labelConfirm: t => t('common_yes'),
    labelClose: t => t('common_no'),
    buttonSet: ButtonSet.CancelAndConfirm,
  });

  if (okClicked) {
    if (isLogin) {
      agePopupOkContentIds.unshift(contentId);
      setLocalStorageObject({
        key: 'viewer_age_popup_ok_content_ids',
        value: agePopupOkContentIds.slice(0, 100),
      });
    } else {
      agePopupOkContentIdsForNonLogin.push(contentId);
    }
  }

  return okClicked;
}

export function setRefs<T>(
  element: T,
  ...refs: Array<
    RefCallback<T> | MutableRefObject<T> | undefined | null | string | boolean
  >
) {
  refs.forEach(ref => {
    if (typeof ref === 'function') {
      ref(element);
    } else if (ref && typeof ref === 'object') {
      ref.current = element;
    }
  });
}

export function getIndexByKey<T>(items: Array<{key: T}>, key: T) {
  return items.findIndex(item => item.key === key);
}

// N일, N시간만 가능. 일과 시간을 동시에 사용하지 않는다.
export function getWaitfreePeriodData(waitfreePeriodByMin: number): {
  timeUnitText: IWaitfreeTimeUnit;
  count: number;
} {
  if (waitfreePeriodByMin >= MINUTES_OF_A_DAY) {
    return {
      timeUnitText: 'day',
      count: Math.floor(waitfreePeriodByMin / MINUTES_OF_A_DAY),
    };
  } else if (waitfreePeriodByMin >= MINUTES_OF_AN_HOUR) {
    return {
      timeUnitText: 'hour',
      count: Math.floor(waitfreePeriodByMin / MINUTES_OF_AN_HOUR),
    };
  } else {
    return {timeUnitText: 'minute', count: waitfreePeriodByMin};
  }
}

export function getWaitfreeTextV4({
  waitfree,
  textKeyMap = TIME_UNIT_TEXT_KEY_MAP,
  showCompleteText = false,
  t,
}: {
  waitfree: IWaitfree;
  textKeyMap?: {[key in IWaitfreeTimeUnit]: string};
  showCompleteText?: boolean;
  t: TFunction;
}) {
  const {intervalMinutes, chargeAtTs, nowFromServer, isCharging, isCharged} =
    waitfree;
  const {timeUnitText, count} = getWaitfreePeriodData(intervalMinutes);
  const periodText = t(textKeyMap[timeUnitText], {count});
  if (isCharging) {
    return {
      periodText,
      chargingText: getRemainText(
        {endDate: chargeAtTs!, now: nowFromServer},
        t
      ),
    };
  } else if (isCharged && showCompleteText) {
    // TODO: V4 번역키 변경
    return {periodText, chargingText: t('contenthome_ticketbar_gidamu_full')};
  } else {
    return {periodText};
  }
}

// TODO: V4 타입이 다르기 때문에 V4 이름을 붙이 함수와 변수를 추가. 나중에 "V4" 제거하자
let localRecentItemsCachedV4: RecentItem[] | undefined;
export function getLocalRecentItemsV4(): RecentItem[] {
  if (!localRecentItemsCachedV4) {
    localRecentItemsCachedV4 =
      getLocalStorageObject<RecentItem[]>({
        key: RECENT_ITEM_LIST_KEY,
      }) || [];
  }
  return localRecentItemsCachedV4.map(item => ({
    ...item,
    content: {
      ...item.content,
      bgColorRgb: convertHexToRgb(item.content.bgColor),
    },
  }));
}
export function getLocalRecentItemByContentIdV4(contentId: number) {
  return getLocalRecentItemsV4().find(item => item.content.id === contentId);
}
export function setLocalRecentItemsV4(list: RecentItem[]) {
  const value = list.slice(0, 30);
  setLocalStorageObject({
    key: RECENT_ITEM_LIST_KEY,
    value,
  });
  localRecentItemsCachedV4 = value;
}

export function getKakaoSdk() {
  return window.Kakao;
}

export const getExpiryDays = ({
  expireDate,
  t,
  isLuckydraw,
}: {
  expireDate: string | IExpireDate;
  t: TFunction;
  isLuckydraw: boolean;
}) => {
  if (typeof expireDate !== 'string') {
    if (expireDate.isoDuration === 'P0D') {
      return isLuckydraw
        ? t('luckydraw_expiry_today')
        : t('notification_expiry_today');
    } else if (expireDate.isoDuration === 'PT0H') {
      return isLuckydraw
        ? t('luckydraw_expiry_hour_1')
        : t('notification_expiry_hour');
    } else {
      return expireDate.days
        ? t(
            isLuckydraw
              ? 'luckydraw_expiry_date'
              : 'notification_expiry_bydate',
            {
              count: expireDate.days,
            }
          )
        : t(
            isLuckydraw
              ? 'luckydraw_expiry_hour'
              : 'notification_expiry_byhour',
            {
              count: expireDate.hours,
            }
          );
    }
  } else {
    return t(
      isLuckydraw
        ? 'luckydraw_expiry_specific'
        : 'notification_valid_specific_date',
      {
        value: expireDate,
      }
    );
  }
};

let localAdultCheckedListCached: IAdultChecked[] | undefined;
function getIsAdultCheckedList() {
  if (!localAdultCheckedListCached) {
    localAdultCheckedListCached =
      getLocalStorageObject<IAdultChecked[]>({
        key: ADULT_CHECKED_LS_KEY,
      }) || [];
  }
  return localAdultCheckedListCached;
}

export function getIsAdultChecked(contentId: number) {
  if (REGION === 'kor') {
    return true;
  }
  const list = getIsAdultCheckedList();
  const item = list.find(item => item.contentId === contentId);
  if (item) {
    const isExpired = item.expire < Date.now();
    if (isExpired) {
      const value = list.filter(item => item.contentId !== contentId);
      setLocalStorageObject({
        key: ADULT_CHECKED_LS_KEY,
        value,
      });
      localAdultCheckedListCached = value;
      return false;
    } else {
      return true;
    }
  } else {
    return false;
  }
}

export function setIsAdultChecked(contentId: number) {
  const list = getIsAdultCheckedList().filter(
    item => item.contentId !== contentId
  );
  list.push({
    contentId,
    expire: Date.now() + SECONDS_OF_A_DAY * 1000,
  });
  setLocalStorageObject({
    key: ADULT_CHECKED_LS_KEY,
    value: list,
  });
  localAdultCheckedListCached = list;
}

interface IAdultChecked {
  contentId: number;
  expire: number; // 0이면 영구, 0이 아니라면 시간 지난 후 다시 요청
}

const ADULT_CHECKED_LS_KEY = 'adult-checked';

// https://podotree.atlassian.net/wiki/spaces/KW/pages/2036073325
export const CS_EMAIL: {
  [key in IRegion]: string;
} = {
  twn: 'support_tw@kakaowebtoon.com',
  tha: 'cs.th@kakaoent.com',
  kor: 'support_kr@kakaowebtoon.com',
  idn: 'support_id@kakaowebtoon.com',
};

export function clearUserInfo(isWithdrawal = false) {
  const store = IS_CLIENT ? getClientStore() : undefined;
  store?.dispatch(authActions.setValue('user', undefined));
  const isPurchaseTabToggled = getLocalStorageBool({
    key: IS_PURCHASE_TAB_TOGGLED_LS_KEY,
  });
  clearLocalStorage();
  deleteCookie(ACCESS_TOKEN_KEY);
  deleteAllLastEpisode();
  isPurchaseTabToggled &&
    setLocalStorageBool({
      key: IS_PURCHASE_TAB_TOGGLED_LS_KEY,
      value: isPurchaseTabToggled,
    });
  location.assign(isWithdrawal ? `/${Page.More}` : '/');
}

export function onRefreshCashHistory() {
  if (IS_CLIENT) {
    const store = getClientStore();
    if (store) {
      store.dispatch(commonActions.fetchUserCashInfo());
      store.dispatch(moreActions.fetchCashHistory('added', true));
    }
  }
}

export function loadScript(src: string, onLoad: () => void) {
  const scriptEl = document.createElement('script');
  document.body.appendChild(scriptEl);
  scriptEl.onload = onLoad;
  scriptEl.type = 'text/javascript';
  scriptEl.async = true;
  scriptEl.src = src;
}

export function getSameTranslationArray(key: string, i18n: i18n) {
  const translatedList = [];
  let index = 1;

  while (i18n.exists(`${key}_${index}`)) {
    translatedList.push(i18n.t(`${key}_${index}`));
    index++;
  }

  return translatedList;
}

export function getIsProhibitedEpisode(errorType: string) {
  return (
    errorType === 'PROHIBITED_EPISODE' || errorType === 'PROHIBITED_CONTENT'
  );
}

const GA_CAMPAIGN_PARAM_KEYS = [
  'utm_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];
const TIARA_CAMPAIGN_PARAMS_KEYS = ['t_src', 't_ch', 't_obj'];
const CAMPAIGN_PARAMS_KEYS = [
  ...GA_CAMPAIGN_PARAM_KEYS,
  ...TIARA_CAMPAIGN_PARAMS_KEYS,
];

export function getCampaignParams(url?: string): ParsedUrlQuery | undefined {
  const queryObj = IS_CLIENT
    ? getRouterQueryObject()
    : url?.includes('?')
      ? getQueryObject(url.split('?')[1])
      : {};
  let campaignParams: ParsedUrlQuery | undefined;
  CAMPAIGN_PARAMS_KEYS.map(key => {
    const query = queryObj[key];
    if (query) {
      !campaignParams && (campaignParams = {});
      campaignParams[key] = query;
    }
  });

  return campaignParams;
}

export function removeGaCampaignParams() {
  return getUrlWithFilteredQuery(
    location.href,
    k => !GA_CAMPAIGN_PARAM_KEYS.includes(k)
  );
}

/**
 * TODO: isAdult undefined 인 경우에 어떻게 처리할지 협의 필요 (ex. 배너 landingUri 가 작품홈인 경우)
 * 만약 서버가 isAdult 정보를 내려주지 않는다면 여기서 API 를 호출해서 isAdult 정보를 받은 후에 letItGo 함수를 호출하자
 */
export function getIsNotAllowedAdultContent({
  isLoginChecking,
  isLogin,
  user,
  pastUrl,
}: {
  isLoginChecking: boolean;
  isLogin: boolean;
  user?: IUser;
  pastUrl?: string;
}) {
  if (isLoginChecking) {
    return true;
  } else if (REGION === 'kor' && !isLogin) {
    showAuthModal({titleType: 'adult'});
    return true;
  } else if (
    REGION === 'kor' &&
    user?.isAdult === false &&
    user?.isIdentityVerified
  ) {
    pToast(t => t('login_adult_toast'));
    return true;
  } else if (!getIsIdVerificationCompleted()) {
    pastUrl && saveReturnPage(pastUrl);
    showIdVerificationModal('adult');
    return true;
  } else {
    return false;
  }
}

// Client에서만 동작
export const callFetchUseTicket = ({
  contentId,
  contentSid,
  id,
  sid,
  type,
  entryType,
  isFree,
}: {
  contentId: number;
  contentSid: string;
  id: number;
  sid: string;
  type: IEpisodeType;
  entryType?: IEntryType;
  isFree: boolean;
}) => {
  const params: IUseTicketParams = {
    contentId,
    contentSid,
    episodeId: id,
    episodeSid: sid,
    entryType,
    isCheckNextEpisode: true,
    authModalTitleType: type === 'waitForFree' ? 'waitfree' : undefined,
    isFree,
  };
  const store = IS_CLIENT ? getClientStore() : undefined;
  store?.dispatch(commonSubActions.fetchUseTicket(params));
};

let promiseCache: Promise<boolean> | undefined;
export function getIsWebpSupported() {
  if (promiseCache !== undefined) {
    return promiseCache;
  }
  promiseCache = new Promise<boolean>(resolve => {
    const image = new Image();
    function testWebp(e: Event | string) {
      // 아래 로직은 Modernizr 참고함
      // https://github.com/Modernizr/Modernizr/blob/400db4043c22af98d46e1d2b9cbc5cb062791192/feature-detects/img/webp.js
      const result =
        typeof e === 'object' && e.type === 'load' ? image.width === 1 : false;
      resolve(result);
    }
    image.onerror = testWebp;
    image.onload = testWebp;
    image.src =
      'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
  });
  return promiseCache;
}

export function makeLoadList(initPage: number, totalPage: number) {
  const result = [initPage];
  for (let i = 1; i < totalPage; i++) {
    if (initPage + i < totalPage) {
      result.push(initPage + i);
    }
    if (initPage - i >= 0) {
      result.push(initPage - i);
    }
  }
  return result;
}

export async function requestImage(
  path: string
): Promise<ArrayBuffer | undefined> {
  return new Promise(resolve => {
    const req = new XMLHttpRequest();
    req.open('GET', path, true);
    req.responseType = 'arraybuffer';
    req.onload = function () {
      const arrayBuffer = req.response;
      resolve(arrayBuffer);
    };
    req.onerror = () => resolve(undefined);
    req.send(null);
  });
}

const MAX_REQ_COUNT = 8;
export function* decryptImages({
  initialPage,
  episodeId,
  onFetchImgFinishedAction,
  encUrls,
  decryptInfo,
  actions,
}: {
  initialPage: number;
  episodeId: number;
  onFetchImgFinishedAction: string;
  encUrls: string[];
  decryptInfo?: IViewerDecryptInfo;
  actions: {
    onFetchImageFinished: () => ReduxAction;
    setImageUrl: ({
      index,
      url,
      episodeId,
    }: {
      index: number;
      url: string;
      episodeId: number;
    }) => ReduxAction;
  };
}) {
  if (!decryptInfo) return;
  const userId = yield* typedSelect(state => state.auth.user?.id);
  const keys: {key?: ArrayBuffer; iv?: ArrayBuffer} | undefined = yield call(
    descriptKeys,
    {...decryptInfo, userId}
  );

  if (keys?.key && keys?.iv) {
    const {key, iv} = keys;
    const images: Array<{index: number; image: ArrayBuffer | undefined}> = [];

    const fetchImage = function* (index: number, loadOrder: number) {
      while (loadOrder >= images.length + MAX_REQ_COUNT) {
        yield take(onFetchImgFinishedAction);
      }

      let image: ArrayBuffer | undefined = yield call(
        requestImage,
        encUrls[index]
      );
      // 실패 시 한 번 더 시도한다
      if (!image) {
        image = yield call(requestImage, encUrls[index]);
      }
      images.push({index, image});
      yield put(actions.onFetchImageFinished());
    };

    const _decryptImages = function* (episodeId: number) {
      const totalImage = encUrls.length;
      let finished = 0;
      while (totalImage > finished) {
        yield delay(1);
        if (finished < images.length) {
          const {index, image} = images[finished];
          if (image) {
            const url: string | undefined = yield call(decryptImage, {
              image,
              key,
              iv,
            });
            if (url) {
              yield put(actions.setImageUrl({index, url, episodeId}));
            }
          }
          finished++;
        }
      }
    };

    const loadList = makeLoadList(
      Math.min(initialPage - 1, encUrls.length - 1),
      encUrls.length
    );
    yield fork(_decryptImages, episodeId);
    // 첫 이미지는 빠르게 처리하기 위해 혼자 호출한다
    yield call(fetchImage, loadList[0], 0);
    yield all(
      loadList
        .slice(1)
        .map((index, loadOrder) => call(fetchImage, index, loadOrder + 1))
    );
  } else {
    // TODO: reporting
  }
}

let isWebpSupported: boolean | undefined;
export function* getViewerResourceParams() {
  if (IS_CLIENT && isWebpSupported === undefined) {
    isWebpSupported = yield call(getIsWebpSupported);
  }
  const nonce = Math.random().toString(36).substring(2);
  const timestamp = Date.now().toString();
  const type = isWebpSupported ? 'AES_CBC_WEBP' : 'AES_CBC_JPEG';
  return {type, nonce, timestamp};
}

// 각각 한군데만 사용하는 함수이지만, 보기 편하도록 한 곳에서 관리한다.
type ILastEpisode = {[key: number]: number};
export function setLastEpisode({
  contentId,
  episodeId,
}: {
  contentId: number;
  episodeId: number;
}) {
  if (!IS_CLIENT) return;
  const lastEpisodes = getLocalStorageObject<ILastEpisode>({
    key: LS_KEY_LAST_EPISODE,
  });
  setLocalStorageObject({
    key: LS_KEY_LAST_EPISODE,
    value: {...lastEpisodes, [contentId]: episodeId},
  });
}

export function getLastEpisode(contentId: number) {
  if (!IS_CLIENT) return;
  const lastEpisodes = getLocalStorageObject<ILastEpisode>({
    key: LS_KEY_LAST_EPISODE,
  });
  return lastEpisodes?.[contentId];
}

export function deleteLastEpisode(contentId: number) {
  if (!IS_CLIENT) return;
  const lastEpisodes = getLocalStorageObject<ILastEpisode>({
    key: LS_KEY_LAST_EPISODE,
  });
  if (lastEpisodes) {
    delete lastEpisodes[contentId];
    setLocalStorageObject({
      key: LS_KEY_LAST_EPISODE,
      value: lastEpisodes,
    });
  }
}

// TODO: 로그인 후, 로그아웃 후에 어떻게 처리하냐에 따라 호출해야할 위치가 달라진다.
// 기획에 확인 후 수정하자. 현재는 clearUserInfo 함수에서 호출한다
export function deleteAllLastEpisode() {
  deleteLocalStorage({key: LS_KEY_LAST_EPISODE});
}

export function* callApiUser(needDetail?: boolean) {
  return call(callApi, {
    apiHost: AUTH_HOST,
    url: `${API_AUTH}/auth/user${needDetail ? '/detail' : ''}`,
    params: {access_token: getAccessToken()},
  });
}
